import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

interface SplitViewContainerProps {
  top: ReactNode;
  bottom: ReactNode;
  bottomVisible: boolean;
  minTopHeight?: number;
  minBottomHeight?: number;
  initialBottomHeight?: number;
}

export function SplitViewContainer({
  top,
  bottom,
  bottomVisible,
  minTopHeight = 260,
  minBottomHeight = 180,
  initialBottomHeight = 260,
}: SplitViewContainerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [bottomHeight, setBottomHeight] = useState(initialBottomHeight);
  const [isDragging, setIsDragging] = useState(false);

  const clampBottomHeight = useCallback(
    (nextHeight: number) => {
      const container = containerRef.current;
      if (!container) {
        return nextHeight;
      }

      const totalHeight = container.clientHeight;
      const maxBottomHeight = Math.max(
        minBottomHeight,
        totalHeight - minTopHeight - 8
      );

      return Math.max(minBottomHeight, Math.min(nextHeight, maxBottomHeight));
    },
    [minBottomHeight, minTopHeight]
  );

  useEffect(() => {
    if (!bottomVisible) {
      setIsDragging(false);
    }
  }, [bottomVisible]);

  useEffect(() => {
    if (!isDragging || !bottomVisible) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const nextBottomHeight = rect.bottom - event.clientY;
      setBottomHeight(clampBottomHeight(nextBottomHeight));
    };

    const onPointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [isDragging, bottomVisible, clampBottomHeight]);

  useEffect(() => {
    const onResize = () => {
      setBottomHeight((current) => clampBottomHeight(current));
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [clampBottomHeight]);

  if (!bottomVisible) {
    return (
      <div
        ref={containerRef}
        className="split-view-container split-view-container--single"
      >
        <div className="split-view-container__top">{top}</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="split-view-container">
      <div
        className="split-view-container__top"
        style={{ flexBasis: `calc(100% - ${bottomHeight + 8}px)` }}
      >
        {top}
      </div>

      <div
        className="split-view-container__divider"
        role="separator"
        aria-orientation="horizontal"
        onPointerDown={() => setIsDragging(true)}
        title="Drag to resize"
      >
        <div className="split-view-container__divider-handle" />
      </div>

      <div
        className="split-view-container__bottom"
        style={{ height: `${bottomHeight}px` }}
      >
        {bottom}
      </div>
    </div>
  );
}